/* libs */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { isEmpty, isEqual, get, debounce } from "lodash";
import { Scope } from "informed";

/* components */
import { Pages, Navigation, Lists, Controls, Buttons, Texts, Extras, Card } from "./StyleGroups";
import { reformThemeData, addDuplicates } from "../changeThemeObject";
import * as globalActions from "../../../../actions/globalActions";
import * as themeActions from "../../../../actions/themesActions";
import { TextInput, SelectInput } from "../../../../components/shared/Forms";
import ChangeHistory from "../../../shared/ChangeHistory";
import formWrapper from "../../../shared/Form";
import Loader from "../../../ui/Loader";
import { getRowHeight } from "./StyleGroups/helper/rowHeight";

class ThemeForm extends Component {
	constructor(props) {
		super(props);

		this.state = {
			currentThemeProperty: null,
			shouldRender: true,
			scopePicked: null,
		};
		this.upsertFirebaseAppetize = debounce(this.upsertFirebaseAppetize, 300, { maxWait: 500 });
	}

	componentDidMount() {
		const { actionType, hasFormApi, getFormApi, formApi, match, themeActions } = this.props;
		if (!hasFormApi) {
			getFormApi(formApi);
		}
		if (actionType !== "new") {
			themeActions.selectTheme(match.params.id);
		}
		const { view } = match.params;
		this.setCurrentThemeProperty(view === "pages" ? "containers" : view);
	}

	componentDidUpdate(prevProps) {
		if (!isEqual(prevProps.values, this.props.values)) {
			this.upsertFirebaseAppetize();
		}
	}

	shouldComponentUpdate(nextProps) {
		const { view: nextView } = nextProps.match.params;
		const { view: prevView } = this.props.match.params;

		if (nextView !== prevView) {
			this.props.upsertTheme(this.props.formApi.getState().values, this.props.formApi);

			//the rerender hack to get subcomponents to rerender is not needed for the card.
			if (nextView !== "card") {
				this.setState({ shouldRender: false, scopePicked: null }, () => {
					this.setState({ shouldRender: true });
				});
			}
			this.setCurrentThemeProperty(nextView === "pages" ? "containers" : nextView);
		}

		return true;
	}

	upsertFirebaseAppetize = () => {
		const id = this.props.values.id
			? this.props.values.id + "-" + this.props.auth.user.uid
			: "new-" + this.props.auth.user.uid;
		const mergedTheme = reformThemeData(this.props.values, addDuplicates);
		this.props.actions.upsertFirebaseObject("theme", id, mergedTheme);
		this.props.setAppetizeThemeId(id);
	};

	checkForEmptyValues = (primaryStatePath, currentStatePath, componentList) => {
		const addPropertyClick = (e, field) => {
			const { formApi } = this.props;
			secondaryData[field] = primaryData[field] || null;
			formApi.setValues({
				...themeData,
			});
			this.upsertFirebaseAppetize();
		};
		const removePropertyClick = (e, field) => {
			const { formApi } = this.props;
			delete secondaryData[field];
			formApi.setValues({
				...themeData,
			});
			this.upsertFirebaseAppetize();
		};

		const { values: themeData } = this.props;
		const primaryData = get(themeData, primaryStatePath);
		const secondaryData = get(themeData, currentStatePath);

		return (
			<React.Fragment>
				{componentList.map(component => {
					const { field, themeBuilderInputType } = component.props;
					const heightClassName = getRowHeight(themeBuilderInputType);

					if (primaryStatePath === currentStatePath || secondaryData.hasOwnProperty(field)) {
						return (
							<div className={`field-row ${heightClassName}`}>
								<div className="component">{component}</div>
								{primaryStatePath !== currentStatePath && (
									<div className="remove-component">
										<button
											className="btn remove-button"
											onClick={e => removePropertyClick(e, field)}
										>
											x
										</button>
									</div>
								)}
							</div>
						);
					} else {
						return (
							<div className={`field-row ${heightClassName}`}>
								<button className="btn add-button" onClick={e => addPropertyClick(e, field)}>
									+
								</button>
							</div>
						);
					}
				})}
			</React.Fragment>
		);
	};

	saveThemeOnScopeChange = e => {
		const { upsertTheme, formApi } = this.props;
		const { scopePicked: currentScopePicked } = this.state;
		const { value: scope } = e.target;

		if (currentScopePicked !== scope) {
			upsertTheme(formApi.getState().values, formApi);
			this.setState({ scopePicked: null }, () => {
				this.setState({ scopePicked: scope });
			});
		}
	};

	renderTexts() {
		const { scopePicked } = this.state;
		const { values: themeData } = this.props;
		const hasThemeData = !isEmpty(themeData);

		const values = [
			{ key: "default", label: "Plain Text", value: "default" },
			{ key: "h1", label: "Heading 1", value: "h1" },
			{ key: "h2", label: "Heading 2", value: "h2" },
			{ key: "h3", label: "Heading 3", value: "h3" },
			{ key: "h4", label: "Heading 4", value: "h4" },
			{ key: "h5", label: "Heading 5", value: "h5" },
			{ key: "h6", label: "Heading 6", value: "h6" },
			{ key: "small", label: "Small", value: "small" },
			{ key: "mini", label: "Mini", value: "mini" },
			{ key: "link", label: "Links", value: "link" },
		];
		const scopePickedExists = values.map(e => e.key).includes(scopePicked);

		if (!scopePicked) {
			this.setState({ scopePicked: "default" });
		}

		return hasThemeData ? (
			<React.Fragment>
				<SelectInput
					field="option"
					id="option"
					placeholder="Option"
					values={values}
					initialValue="default"
					onChange={this.saveThemeOnScopeChange}
				/>
				{scopePickedExists && (
					<Scope scope="texts">
						<Scope scope={scopePicked}>
							<Texts
								themeData={themeData}
								scopePicked={scopePicked}
								primaryState="normal"
								checkForEmptyValues={this.checkForEmptyValues}
							/>
						</Scope>
					</Scope>
				)}
			</React.Fragment>
		) : (
			<Loader />
		);
	}

	renderButtons() {
		const { scopePicked } = this.state;
		const { values: themeData } = this.props;
		const hasThemeData = !isEmpty(themeData);

		const values = [
			{ key: "primary", label: "Primary", value: "primary" },
			{ key: "secondary", label: "Secondary", value: "secondary" },
			{ key: "success", label: "Success", value: "success" },
			{ key: "danger", label: "Danger", value: "danger" },
			{ key: "warning", label: "Warning", value: "warning" },
			{ key: "info", label: "Info", value: "info" },
			{ key: "light", label: "Light", value: "light" },
			{ key: "dark", label: "Dark", value: "dark" },
			{ key: "textOnly", label: "Text Only", value: "textOnly" },
		];
		const scopePickedExists = values.map(e => e.key).includes(scopePicked);

		if (!scopePicked) {
			this.setState({ scopePicked: "primary" });
		}

		return hasThemeData ? (
			<React.Fragment>
				<SelectInput
					field="option"
					id="option"
					placeholder="Option"
					values={values}
					initialValue="primary"
					onChange={this.saveThemeOnScopeChange}
				/>
				{scopePickedExists && (
					<Scope scope="buttons">
						<Scope scope={scopePicked}>
							<Buttons
								themeData={themeData}
								scopePicked={scopePicked}
								primaryState="enabled"
								checkForEmptyValues={this.checkForEmptyValues}
							/>
						</Scope>
					</Scope>
				)}
			</React.Fragment>
		) : (
			<Loader />
		);
	}

	renderCard() {
		const { values: themeData } = this.props;
		if (!themeData.card) {
			return <Loader />;
		}

		return (
			<Scope scope="card">
				<Card />
			</Scope>
		);
	}

	renderControls() {
		const { scopePicked } = this.state;
		const { values: themeData } = this.props;
		const hasThemeData = !isEmpty(themeData);

		const values = [
			{ key: "text", label: "Text", value: "text" },
			{ key: "picker", label: "Picker", value: "picker" },
			{ key: "radioCheckbox", label: "Radio / Checkbox", value: "radioCheckbox" },
		];
		const scopePickedExists = values.map(e => e.key).includes(scopePicked);

		if (!scopePicked) {
			this.setState({ scopePicked: "text" });
		}

		return hasThemeData ? (
			<React.Fragment>
				<SelectInput
					field="option"
					id="option"
					placeholder="Option"
					values={values}
					initialValue="text"
					onChange={this.saveThemeOnScopeChange}
				/>
				{scopePickedExists && (
					<Scope scope="controls">
						<Scope scope={scopePicked}>
							<Controls
								themeData={themeData}
								scopePicked={scopePicked}
								primaryState="enabled"
								checkForEmptyValues={this.checkForEmptyValues}
							/>
						</Scope>
					</Scope>
				)}
			</React.Fragment>
		) : (
			<Loader />
		);
	}

	renderLists() {
		const { scopePicked } = this.state;
		const { values: themeData } = this.props;
		const hasThemeData = !isEmpty(themeData);

		const values = [
			{ key: "header", label: "Header", value: "header" },
			{ key: "footer", label: "Footer", value: "footer" },
			{ key: "item", label: "Item", value: "item" },
			{ key: "separator", label: "Separator", value: "separator" },
		];
		const scopePickedExists = values.map(e => e.key).includes(scopePicked);

		if (!scopePicked) {
			this.setState({ scopePicked: "header" });
		}

		return hasThemeData ? (
			<React.Fragment>
				<SelectInput
					field="option"
					id="option"
					placeholder="Option"
					values={values}
					initialValue="header"
					onChange={this.saveThemeOnScopeChange}
				/>
				{scopePickedExists && (
					<Scope scope="lists">
						<Lists
							themeData={themeData}
							scopePicked={scopePicked}
							primaryState="default"
							checkForEmptyValues={this.checkForEmptyValues}
						/>
					</Scope>
				)}
			</React.Fragment>
		) : (
			<Loader />
		);
	}

	renderPages() {
		const { scopePicked } = this.state;
		const { values: themeData } = this.props;
		const hasThemeData = !isEmpty(themeData);

		const values = [
			{ key: "page", label: "Page", value: "page" },
			{ key: "navBar", label: "Nav Bar", value: "navBar" },
		];
		const scopePickedExists = values.map(e => e.key).includes(scopePicked);

		if (!scopePicked) {
			this.setState({ scopePicked: "page" });
		}

		return hasThemeData ? (
			<React.Fragment>
				<SelectInput
					field="option"
					id="option"
					placeholder="Option"
					values={values}
					initialValue="page"
					onChange={this.saveThemeOnScopeChange}
				/>
				{scopePickedExists && (
					<Pages themeData={{ page: themeData.page, navBar: themeData.navBar }} scope={scopePicked} />
				)}
			</React.Fragment>
		) : (
			<Loader />
		);
	}

	renderNavigation() {
		const { scopePicked } = this.state;
		const { values: themeData } = this.props;
		const hasThemeData = !isEmpty(themeData);

		const values = [
			{ key: "container", label: "Container", value: "container" },
			{ key: "item", label: "Item", value: "item" },
			{ key: "subItem", label: "Sub Item", value: "subItem" },
			{ key: "tabBar", label: "Tab Bar", value: "tabBar" },
			{ key: "title", label: "Title", value: "title" },
		];
		const scopePickedExists = values.map(e => e.key).includes(scopePicked);

		if (!scopePicked) {
			this.setState({ scopePicked: "container" });
		}

		return hasThemeData ? (
			<React.Fragment>
				<SelectInput
					field="option"
					id="option"
					placeholder="Option"
					values={values}
					initialValue="container"
					onChange={this.saveThemeOnScopeChange}
				/>
				{scopePickedExists && (
					<Navigation
						themeData={{ navigation: themeData.navigation, tabBar: themeData.tabBar }}
						scopePicked={scopePicked}
						primaryState="enabled"
						checkForEmptyValues={this.checkForEmptyValues}
					/>
				)}
			</React.Fragment>
		) : (
			<Loader />
		);
	}

	renderExtras() {
		return (
			<div className="ta-center">
				<Extras />
			</div>
		);
	}

	setCurrentThemeProperty = property => {
		this.setState({ currentThemeProperty: property });
		this.props.pickAppetizePage(property);
		if (property === null) {
			this.props.upsertTheme(this.props.formApi.getState().values, this.props.formApi);
		}
	};

	render() {
		const {
			actionType,
			id,
			match: {
				params: { view },
			},
			responsive: { isResponsive },
		} = this.props;
		let content = null;
		switch (view) {
			case "pages":
				content = this.renderPages();
				break;
			case "navigation":
				content = this.renderNavigation();
				break;
			case "lists":
				content = this.renderLists();
				break;
			case "controls":
				content = this.renderControls();
				break;
			case "buttons":
				content = this.renderButtons();
				break;
			case "card":
				content = this.renderCard();
				break;
			case "texts":
				content = this.renderTexts();
				break;
			case "extra":
				content = this.renderExtras();
				break;
			default:
				content = this.renderPages();
				break;
		}
		return content ? (
			<div className="theme-form">
				{actionType === "update" && !!id && <ChangeHistory id={id} entity="themes" />}
				<div className="theme-top-info">
					<div className="theme-name">
						<TextInput
							field="name"
							id="name"
							placeholder="Theme Name"
							autocomplete="off"
							isRequired={true}
							style={{ paddingBottom: 0 }}
						/>
					</div>
					<div className="theme-save">
						<div
							className={`btn btn-primary${isResponsive ? " btn-block" : ""}`}
							onClick={() => this.props.submitForm(true)}
						>
							Save Theme
						</div>
					</div>
				</div>
				<hr />
				{view === "navigation" && (
					<div style={{ marginTop: 12 }}>
						<SelectInput
							field="navigationType"
							id="navigationType"
							placeholder="Navigation Type"
							values={[
								{ key: "sideMenu", value: "sideMenu", label: "Side Menu" },
								{ key: "twoColumnGrid", value: "twoColumnGrid", label: "Two Column Grid" },
								{ key: "threeColumnGrid", value: "threeColumnGrid", label: "Three Column Grid" },
								{ key: "fullScreen", value: "fullScreen", label: "Full Screen" },
							]}
							initialValue={this.props.navigationType || "sideMenu"}
							onChange={e => this.props.setNavigationType(e.target.value)}
						/>
					</div>
				)}
				{this.state.shouldRender && content}
			</div>
		) : (
			<div>Loading...</div>
		);
	}
}

ThemeForm.propTypes = {
	actions: PropTypes.object,
	actionType: PropTypes.string,
	auth: PropTypes.object,
	formApi: PropTypes.object,
	getFormApi: PropTypes.any,
	hasFormApi: PropTypes.bool,
	id: PropTypes.string,
	navigationType: PropTypes.string,
	pickAppetizePage: PropTypes.func,
	setAppetizeThemeId: PropTypes.func,
	setNavigationType: PropTypes.func,
	submitForm: PropTypes.func,
	upsertTheme: PropTypes.func,
	values: PropTypes.object,
};

const mapStateToProps = state => ({
	apps: state.apps,
	auth: state.auth,
	pages: state.pages,
	responsive: state.responsive,
	themes: state.themes,
});

const mapDispatchToProps = dispatch => ({
	actions: bindActionCreators(globalActions, dispatch),
	themeActions: bindActionCreators(themeActions, dispatch),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(formWrapper(ThemeForm));
